import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2pdf from 'html2pdf.js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './components/ui/tabs';
import { useDemoData } from '../context/DemoDataContext';
import { useAppSelector } from '../store/hooks';
import { selectAuthToken } from '../store/selectors/authSelectors';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Skeleton } from './components/ui/skeleton';
import { Slider } from './components/ui/slider';
import { Switch } from './components/ui/switch';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './components/ui/table';
import { toast } from 'sonner';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  DollarSign,
  AlertTriangle,
  FileText,
  Download,
  Sparkles,
  Loader2,
  Users,
  TrendingUp,
  Activity,
  BarChart3,
  Calculator,
  Sliders,
  ShieldCheck,
  CheckCircle2,
  Layers,
  Info,
  Save,
  Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}`;

const getRiskColor = (category) => {
  switch (category) {
    case 'Low': return 'text-green-500';
    case 'Medium': return 'text-yellow-500';
    case 'High': return 'text-red-500';
    default: return 'text-muted-foreground';
  }
};

const getRiskBgColor = (category) => {
  switch (category) {
    case 'Low': return 'bg-green-500/10';
    case 'Medium': return 'bg-yellow-500/10';
    case 'High': return 'bg-red-500/10';
    default: return 'bg-muted';
  }
};

export function ClientDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [summary, setSummary] = useState(null);
  const { demoClients, isDemoMode } = useDemoData();
  const token = useAppSelector(selectAuthToken);

  // Active Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Custom Ad-Hoc Assessment State
  const [adHocParams, setAdHocParams] = useState({
    managementExp: 5,
    collateralValue: 10,
    marketPosition: 75,
    isAudited: true,
    hasGuarantee: true,
    isEsgCompliant: true,
  });
  const [adHocNotes, setAdHocNotes] = useState('');
  const [isNotesSaved, setIsNotesSaved] = useState(false);

  const getTranslatedStatus = (status) => {
    switch (status) {
      case 'active': return t('clients.active');
      case 'pending': return t('clients.pending');
      case 'defaulted': return t('clients.defaulted');
      case 'closed': return t('clients.closed');
      default: return status;
    }
  };

  const getTranslatedRisk = (category) => {
    switch (category) {
      case 'Low': return t('clients.lowRisk');
      case 'Medium': return t('clients.mediumRisk');
      case 'High': return t('clients.highRisk');
      default: return category;
    }
  };

  const getTranslatedLoanType = (loanType) => {
    switch (loanType) {
      case 'Working Capital': return t('clients.workingCapital');
      case 'Business Expansion': return t('clients.businessExpansion');
      case 'Equipment Financing': return t('clients.equipmentFinancing');
      case 'Fleet Financing': return t('clients.fleetFinancing');
      case 'Project Financing': return t('clients.projectFinancing');
      case 'Renovation Loan': return t('clients.renovationLoan');
      default: return loanType;
    }
  };

  const getTranslatedIndustry = (ind) => {
    switch (ind) {
      case 'Technology': return t('clients.technology');
      case 'Healthcare': return t('clients.healthcare');
      case 'Finance': return t('clients.finance');
      case 'Retail': return t('clients.retail');
      case 'Manufacturing': return t('clients.manufacturing');
      case 'Real Estate': return t('clients.realEstate');
      default: return ind || 'N/A';
    }
  };

  const fetchClient = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients/${id}`);
      setClient(response.data);
    } catch (error) {
      if (isDemoMode || (demoClients && demoClients.length > 0)) {
        const demoFound = demoClients?.find(c => String(c.id) === String(id) || String(c._id) === String(id));
        if (demoFound) {
          setClient(demoFound);
          return;
        }
      }
      toast.error('Client not found');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, isDemoMode, demoClients]);

  useEffect(() => {
    fetchClient();
  }, [id, fetchClient]);

  const generateSummary = async () => {
    setGenerating(true);
    setSummary(null);
    try {
      const response = await axios.post(
        `${API}/ai/summary`,
        { client_id: id },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 90000
        }
      );
      setSummary(response.data);
      toast.success('Summary generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate summary');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdf = async () => {
    if (!summary) {
      toast.error('Please generate a summary first to download as PDF.');
      return;
    }
    setDownloadingPdf(true);
    try {
      const element = document.getElementById('summary-content');
      if (!element) throw new Error('Summary content not found');

      const opt = {
        margin: 0.5,
        filename: `finai_summary_${client.company_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      await html2pdf().set(opt).from(element).save();
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Analytics tab computed datasets
  const analyticsData = useMemo(() => {
    if (!client) return [];
    const rev = client.annual_revenue ? client.annual_revenue / 12 : 250000;
    const loanPayment = (client.loan_amount || 500000) / 36;
    return [
      { month: 'Jan', revenue: Math.round(rev * 0.9), repayment: Math.round(loanPayment), cashFlow: Math.round(rev * 0.4) },
      { month: 'Feb', revenue: Math.round(rev * 0.95), repayment: Math.round(loanPayment), cashFlow: Math.round(rev * 0.42) },
      { month: 'Mar', revenue: Math.round(rev * 1.05), repayment: Math.round(loanPayment), cashFlow: Math.round(rev * 0.48) },
      { month: 'Apr', revenue: Math.round(rev * 1.0), repayment: Math.round(loanPayment), cashFlow: Math.round(rev * 0.45) },
      { month: 'May', revenue: Math.round(rev * 1.12), repayment: Math.round(loanPayment), cashFlow: Math.round(rev * 0.52) },
      { month: 'Jun', revenue: Math.round(rev * 1.08), repayment: Math.round(loanPayment), cashFlow: Math.round(rev * 0.50) },
    ];
  }, [client]);

  const riskDimensions = useMemo(() => {
    if (!client) return [];
    const baseScore = client.risk_score || 50;
    return [
      { category: 'Liquidity & Cash Flow', score: Math.min(100, Math.max(20, 100 - baseScore + 10)), status: 'Healthy' },
      { category: 'Debt Service Coverage', score: Math.min(100, Math.max(20, 100 - baseScore - 5)), status: 'Optimal' },
      { category: 'Credit History & Record', score: Math.min(100, Math.max(20, 100 - baseScore + 15)), status: 'Strong' },
      { category: 'Market & Industry Stability', score: Math.min(100, Math.max(20, 100 - baseScore)), status: 'Moderate' },
      { category: 'Operational Resilience', score: Math.min(100, Math.max(20, 100 - baseScore + 5)), status: 'High' },
    ];
  }, [client]);

  const transactionHistory = useMemo(() => {
    if (!client) return [];
    const monthlyPayment = Math.round((client.loan_amount || 500000) / 36);
    return [
      { id: 'TXN-9021', date: '2026-05-01', amount: monthlyPayment, type: 'Loan Repayment', status: 'Completed', method: 'ACH Direct Debit' },
      { id: 'TXN-8742', date: '2026-04-01', amount: monthlyPayment, type: 'Loan Repayment', status: 'Completed', method: 'ACH Direct Debit' },
      { id: 'TXN-8411', date: '2026-03-01', amount: monthlyPayment, type: 'Loan Repayment', status: 'Completed', method: 'Wire Transfer' },
      { id: 'TXN-8109', date: '2026-02-01', amount: monthlyPayment, type: 'Loan Repayment', status: 'Completed', method: 'ACH Direct Debit' },
      { id: 'TXN-7854', date: '2026-01-01', amount: monthlyPayment, type: 'Loan Repayment', status: 'Completed', method: 'ACH Direct Debit' },
    ];
  }, [client]);

  // Scoring tab computed dynamic Ad-Hoc score
  const calculatedAdHocScore = useMemo(() => {
    if (!client) return 50;
    const base = client.risk_score || 50;
    const adjustment = (
      Number(adHocParams.managementExp) +
      Number(adHocParams.collateralValue) +
      Math.round((adHocParams.marketPosition - 50) / 5) +
      (adHocParams.isAudited ? 5 : -5) +
      (adHocParams.hasGuarantee ? 10 : 0) +
      (adHocParams.isEsgCompliant ? 5 : 0)
    );
    return Math.min(100, Math.max(1, base + adjustment));
  }, [client, adHocParams]);

  const scoreDelta = useMemo(() => {
    if (!client) return 0;
    return calculatedAdHocScore - (client.risk_score || 50);
  }, [client, calculatedAdHocScore]);

  const handleSaveNotes = () => {
    setIsNotesSaved(true);
    toast.success('Ad-Hoc evaluation saved successfully!');
    setTimeout(() => setIsNotesSaved(false), 3000);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="client-detail-loading">
        <Skeleton className="h-10 w-32 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-64 rounded-xl md:col-span-2" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="client-detail-page">
      {/* Back Button & Top Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/clients')}
          data-testid="back-to-clients"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('clientDetail.backToClients')}
        </Button>
      </div>

      {/* Main Tabs Container */}
      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 bg-muted/70 p-1 rounded-xl">
          <TabsTrigger value="overview" className="flex items-center gap-2 font-medium">
            <FileText className="w-4 h-4 text-primary" />
            {t('clientDetail.tabOverview')}
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2 font-medium">
            <BarChart3 className="w-4 h-4 text-primary" />
            {t('clientDetail.tabAnalytics')}
          </TabsTrigger>
          <TabsTrigger value="scoring" className="flex items-center gap-2 font-medium">
            <Calculator className="w-4 h-4 text-primary" />
            {t('clientDetail.tabScoring')}
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: OVERVIEW & AI SUMMARY */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Header Card */}
              <Card data-testid="client-header-card">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h1 className="font-heading text-2xl font-bold">{client.company_name}</h1>
                        <Badge variant={client.status === 'active' ? 'default' : client.status === 'defaulted' ? 'destructive' : 'secondary'}>
                          {getTranslatedStatus(client.status)}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground">{client.name}</p>
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {client.email}
                        </div>
                        {client.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {client.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Loan Details */}
              <Card data-testid="loan-details-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">{t('clientDetail.loanDetails')}</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">{t('clientDetail.loanAmount')}</p>
                    <p className="font-heading font-bold text-lg">{formatCurrency(client.loan_amount)}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">{t('clientDetail.loanType')}</p>
                    <p className="font-heading font-bold text-lg">{getTranslatedLoanType(client.loan_type)}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">{t('clientDetail.industry')}</p>
                    <p className="font-heading font-bold text-lg">{getTranslatedIndustry(client.industry)}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">{t('clientDetail.created')}</p>
                    <p className="font-heading font-bold text-sm">{formatDate(client.created_at)}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Company Info */}
              <Card data-testid="company-info-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">{t('clientDetail.companyInfo')}</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-muted/50">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('clientDetail.annualRevenue')}</p>
                      <p className="font-heading font-semibold">{formatCurrency(client.annual_revenue)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-muted/50">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('clientDetail.employees')}</p>
                      <p className="font-heading font-semibold">{client.employees || 'N/A'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Risk Score Card */}
              <Card data-testid="risk-score-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">{t('clientDetail.riskAssessment')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`p-6 rounded-xl ${getRiskBgColor(client.risk_category)} text-center mb-4`}>
                    <p className="text-sm text-muted-foreground mb-2">{t('clientDetail.riskScore')}</p>
                    <p className={`font-heading text-4xl font-bold ${getRiskColor(client.risk_category)}`}>
                      {client.risk_score}
                    </p>
                    <p className="text-sm text-muted-foreground">/100</p>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <AlertTriangle className={`w-5 h-5 ${getRiskColor(client.risk_category)}`} />
                    <span className={`font-semibold ${getRiskColor(client.risk_category)}`}>
                      {getTranslatedRisk(client.risk_category)} {t('clientDetail.riskLevel')}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* AI Summary Card */}
              <Card data-testid="ai-summary-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-secondary" />
                    {t('clientDetail.aiSummary')}
                  </CardTitle>
                  <CardDescription>{t('clientDetail.aiSummaryDesc')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button
                    onClick={generateSummary}
                    disabled={generating}
                    className="w-full bg-gradient-to-r from-[#5B7BB5] to-[#E573A0] hover:opacity-90"
                    data-testid="generate-summary-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('clientDetail.generatingBtn')}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        {t('clientDetail.generateSummaryBtn')}
                      </>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={downloadPdf}
                    disabled={downloadingPdf || !summary}
                    className="w-full"
                    data-testid="download-pdf-btn"
                  >
                    {downloadingPdf ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('clientDetail.creatingPdfBtn')}
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        {t('clientDetail.downloadPdfBtn')}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* AI Summary Display */}
          {summary && (
            <Card id="summary-content" className="mt-6" data-testid="summary-result-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Executive Summary
                  </CardTitle>
                  <span className="text-xs text-muted-foreground">
                    Generated {new Date(summary.generated_at).toLocaleString()}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {summary.summary}
                  </ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB 2: GRAPHICS & ANALYTICS */}
        <TabsContent value="analytics" className="space-y-6">
          {/* Key Financial Metrics Banner */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="hover:border-primary/30 transition-colors">
              <CardContent className="p-5">
                <p className="text-xs text-muted-foreground mb-1">{t('clientDetail.dscrLabel')}</p>
                <p className="text-2xl font-bold font-heading">1.85x</p>
                <span className="inline-block mt-2 text-xs text-green-500 bg-green-500/10 px-2 py-0.5 rounded font-medium">
                  {t('clientDetail.dscrStatus')}
                </span>
              </CardContent>
            </Card>

            <Card className="hover:border-primary/30 transition-colors">
              <CardContent className="p-5">
                <p className="text-xs text-muted-foreground mb-1">{t('clientDetail.workingCapitalLabel')}</p>
                <p className="text-2xl font-bold font-heading">{formatCurrency((client.annual_revenue || 3000000) * 0.25)}</p>
                <span className="inline-block mt-2 text-xs text-blue-500 bg-blue-500/10 px-2 py-0.5 rounded font-medium">
                  {t('clientDetail.workingCapitalStatus')}
                </span>
              </CardContent>
            </Card>

            <Card className="hover:border-primary/30 transition-colors">
              <CardContent className="p-5">
                <p className="text-xs text-muted-foreground mb-1">{t('clientDetail.onTimePaymentLabel')}</p>
                <p className="text-2xl font-bold font-heading">98.5%</p>
                <span className="inline-block mt-2 text-xs text-green-500 bg-green-500/10 px-2 py-0.5 rounded font-medium">
                  {t('clientDetail.onTimePaymentStatus')}
                </span>
              </CardContent>
            </Card>

            <Card className="hover:border-primary/30 transition-colors">
              <CardContent className="p-5">
                <p className="text-xs text-muted-foreground mb-1">{t('clientDetail.dtiLabel')}</p>
                <p className="text-2xl font-bold font-heading">
                  {((client.loan_amount / (client.annual_revenue || 1000000)) * 100).toFixed(1)}%
                </p>
                <span className="inline-block mt-2 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded font-medium">
                  {t('clientDetail.dtiStatus')}
                </span>
              </CardContent>
            </Card>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue & Loan Repayment History Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  {t('clientDetail.monthlyTrendsChartTitle')}
                </CardTitle>
                <CardDescription>{t('clientDetail.monthlyTrendsChartDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="h-72">
                <div style={{ width: '100%', height: 260 }}>
                  <BarChart
                    dataset={analyticsData}
                    xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
                    series={[
                      { dataKey: 'revenue', label: t('clientDetail.revenueSeriesLabel'), color: '#5B7BB5' },
                      { dataKey: 'repayment', label: t('clientDetail.repaymentSeriesLabel'), color: '#E573A0' },
                    ]}
                    margin={{ left: 55, right: 20, top: 20, bottom: 40 }}
                    height={250}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Risk Factor Breakdown Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary" />
                  {t('clientDetail.riskDimensionTitle')}
                </CardTitle>
                <CardDescription>{t('clientDetail.riskDimensionDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="h-72">
                <div style={{ width: '100%', height: 260 }}>
                  <BarChart
                    dataset={riskDimensions}
                    xAxis={[{ scaleType: 'band', dataKey: 'category', tickLabelStyle: { fontSize: 10, angle: -15, textAnchor: 'end' } }]}
                    series={[{ dataKey: 'score', label: t('clientDetail.categoryScoreLabel'), color: '#10B981' }]}
                    margin={{ left: 45, right: 20, top: 20, bottom: 55 }}
                    height={250}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Client Transaction History Data Grid */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Layers className="w-5 h-5 text-primary" />
                {t('clientDetail.recentTransactionsTitle')}
              </CardTitle>
              <CardDescription>{t('clientDetail.recentTransactionsDesc')} - {client.company_name}</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('clientDetail.colTxId')}</TableHead>
                    <TableHead>{t('clientDetail.colDate')}</TableHead>
                    <TableHead>{t('clientDetail.colType')}</TableHead>
                    <TableHead>{t('clientDetail.colMethod')}</TableHead>
                    <TableHead>{t('clientDetail.colAmount')}</TableHead>
                    <TableHead className="text-right">{t('clientDetail.colStatus')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactionHistory.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{row.id}</TableCell>
                      <TableCell className="text-sm">{row.date}</TableCell>
                      <TableCell className="text-sm font-medium">{row.type}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.method}</TableCell>
                      <TableCell className="font-medium">{formatCurrency(row.amount)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-200">
                          {row.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 3: FINAI & AD-HOC SCORING */}
        <TabsContent value="scoring" className="space-y-6">
          {/* Side-by-Side Score Comparison Header */}
          <Card className="bg-gradient-to-r from-muted/80 via-background to-muted/80 border-primary/20">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <ShieldCheck className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold">{t('clientDetail.scoringHeaderTitle')}</h2>
                    <p className="text-sm text-muted-foreground">
                      {t('clientDetail.scoringHeaderDesc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6 bg-background p-4 rounded-xl border border-border shadow-sm">
                  {/* Official FinAI Score */}
                  <div className="text-center px-4 border-r border-border">
                    <p className="text-xs text-muted-foreground mb-1">{t('clientDetail.standardScoreLabel')}</p>
                    <p className="text-3xl font-bold font-heading text-primary">{client.risk_score}</p>
                    <p className="text-xs text-muted-foreground">{t('clientDetail.outOf100')}</p>
                  </div>

                  {/* Custom Ad-Hoc Score */}
                  <div className="text-center px-4">
                    <p className="text-xs text-muted-foreground mb-1">{t('clientDetail.customAdHocScoreLabel')}</p>
                    <p className="text-3xl font-bold font-heading text-secondary">{calculatedAdHocScore}</p>
                    <p className="text-xs text-muted-foreground">{t('clientDetail.adjustedScore')}</p>
                  </div>

                  {/* Variance Badge */}
                  <div>
                    <Badge variant={scoreDelta >= 0 ? 'default' : 'destructive'} className="text-xs font-semibold px-3 py-1">
                      {scoreDelta >= 0 ? `+${scoreDelta} ${t('clientDetail.favorablePts')}` : `${scoreDelta} ${t('clientDetail.conservativePts')}`}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* COLUMN 1: Official FinAI Score Parameters */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  {t('clientDetail.finAiParametersTitle')}
                </CardTitle>
                <CardDescription>
                  {t('clientDetail.finAiParametersDesc')} ({client.risk_score}/100)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-xl bg-muted/40 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{t('clientDetail.creditBureauIndex')}</p>
                      <Badge variant="outline" className="text-xs">{t('clientDetail.weight')}: 25%</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t('clientDetail.creditBureauDesc')}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm">750 / 850</p>
                    <span className="text-xs text-green-500 font-medium">+22 pts</span>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-muted/40 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{t('clientDetail.dscrLabel')}</p>
                      <Badge variant="outline" className="text-xs">{t('clientDetail.weight')}: 20%</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t('clientDetail.dscrDesc')}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm">1.85x</p>
                    <span className="text-xs text-green-500 font-medium">+18 pts</span>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-muted/40 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{t('clientDetail.liquidityRatio')}</p>
                      <Badge variant="outline" className="text-xs">{t('clientDetail.weight')}: 20%</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t('clientDetail.liquidityDesc')}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm">1.45 Ratio</p>
                    <span className="text-xs text-green-500 font-medium">+16 pts</span>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-muted/40 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{t('clientDetail.historicalDefaultRate')}</p>
                      <Badge variant="outline" className="text-xs">{t('clientDetail.weight')}: 15%</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t('clientDetail.historicalDefaultDesc')}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm">0 Defaults</p>
                    <span className="text-xs text-green-500 font-medium">+15 pts</span>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-muted/40 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{t('clientDetail.industrySectorRisk')}</p>
                      <Badge variant="outline" className="text-xs">{t('clientDetail.weight')}: 10%</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{client.industry || 'Technology'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm">Low Volatility</p>
                    <span className="text-xs text-blue-500 font-medium">+8 pts</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* COLUMN 2: Custom Ad-Hoc Score Calculator */}
            <Card className="border-secondary/30">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Sliders className="w-5 h-5 text-secondary" />
                  {t('clientDetail.adHocTitle')}
                </CardTitle>
                <CardDescription>
                  {t('clientDetail.adHocDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Parameter 1: Management Experience */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">{t('clientDetail.managementExp')}</Label>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      type="button"
                      variant={adHocParams.managementExp === 10 ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAdHocParams(p => ({ ...p, managementExp: 10 }))}
                    >
                      {t('clientDetail.exceptional')}
                    </Button>
                    <Button
                      type="button"
                      variant={adHocParams.managementExp === 5 ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAdHocParams(p => ({ ...p, managementExp: 5 }))}
                    >
                      {t('clientDetail.experienced')}
                    </Button>
                    <Button
                      type="button"
                      variant={adHocParams.managementExp === -10 ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAdHocParams(p => ({ ...p, managementExp: -10 }))}
                    >
                      {t('clientDetail.limited')}
                    </Button>
                  </div>
                </div>

                {/* Parameter 2: Collateral & Assets */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">{t('clientDetail.collateralQuality')}</Label>
                    <span className="text-xs text-secondary font-semibold">
                      {adHocParams.collateralValue === 15 ? `${t('clientDetail.fullySecured')} (+15)` : adHocParams.collateralValue === 5 ? `${t('clientDetail.partial')} (+5)` : `${t('clientDetail.unsecured')} (-15)`}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      type="button"
                      variant={adHocParams.collateralValue === 15 ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAdHocParams(p => ({ ...p, collateralValue: 15 }))}
                    >
                      {t('clientDetail.fullySecured')}
                    </Button>
                    <Button
                      type="button"
                      variant={adHocParams.collateralValue === 5 ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAdHocParams(p => ({ ...p, collateralValue: 5 }))}
                    >
                      {t('clientDetail.partial')}
                    </Button>
                    <Button
                      type="button"
                      variant={adHocParams.collateralValue === -15 ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAdHocParams(p => ({ ...p, collateralValue: -15 }))}
                    >
                      {t('clientDetail.unsecured')}
                    </Button>
                  </div>
                </div>

                {/* Parameter 3: Market Sentiment Slider */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">{t('clientDetail.marketConditions')} ({adHocParams.marketPosition}/100)</Label>
                    <span className="text-xs text-muted-foreground">
                      Impact: {Math.round((adHocParams.marketPosition - 50) / 5) >= 0 ? `+${Math.round((adHocParams.marketPosition - 50) / 5)}` : Math.round((adHocParams.marketPosition - 50) / 5)} pts
                    </span>
                  </div>
                  <Slider
                    value={[adHocParams.marketPosition]}
                    onValueChange={([val]) => setAdHocParams(p => ({ ...p, marketPosition: val }))}
                    min={0}
                    max={100}
                    step={5}
                  />
                </div>

                {/* Parameter 4: Toggle Switches */}
                <div className="space-y-3 pt-2 border-t border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">{t('clientDetail.cpaAudit')}</Label>
                      <p className="text-xs text-muted-foreground">{t('clientDetail.cpaAuditDesc')}</p>
                    </div>
                    <Switch
                      checked={adHocParams.isAudited}
                      onCheckedChange={(checked) => setAdHocParams(p => ({ ...p, isAudited: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">{t('clientDetail.parentGuarantee')}</Label>
                      <p className="text-xs text-muted-foreground">{t('clientDetail.parentGuaranteeDesc')}</p>
                    </div>
                    <Switch
                      checked={adHocParams.hasGuarantee}
                      onCheckedChange={(checked) => setAdHocParams(p => ({ ...p, hasGuarantee: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">{t('clientDetail.esgCert')}</Label>
                      <p className="text-xs text-muted-foreground">{t('clientDetail.esgCertDesc')}</p>
                    </div>
                    <Switch
                      checked={adHocParams.isEsgCompliant}
                      onCheckedChange={(checked) => setAdHocParams(p => ({ ...p, isEsgCompliant: checked }))}
                    />
                  </div>
                </div>

                {/* Evaluator Notes & Save */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-sm font-medium">{t('clientDetail.notesLabel')}</Label>
                  <Textarea
                    placeholder={t('clientDetail.notesPlaceholder')}
                    value={adHocNotes}
                    onChange={(e) => setAdHocNotes(e.target.value)}
                    rows={3}
                  />
                  <Button
                    onClick={handleSaveNotes}
                    className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground mt-2"
                  >
                    {isNotesSaved ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        {t('clientDetail.savedBadge')}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {t('clientDetail.saveEvaluationBtn')}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
